import { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckoutForm } from '../components/checkout/CheckoutForm';
import { ProductCard } from '../components/checkout/ProductCard';
import { checkoutService } from '../services/checkout';
import type { CouponData } from '../types';
import { createLogger } from '../utils/logger';
import type { Checkout } from '../types/checkout';

const logger = createLogger('CheckoutPage');

export function CheckoutPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [checkout, setCheckout] = useState<Checkout | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [appliedCoupon, setAppliedCoupon] = useState<CouponData>({
    code: '',
    discount: 0,
    isApplied: false,
  });

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    'CREDIT_CARD' | 'PIX' | 'BOLETO'
  >('CREDIT_CARD');

  const handleCouponChange = (coupon: CouponData) => {
    setAppliedCoupon(coupon);
  };

  const handlePaymentMethodChange = (
    method: 'CREDIT_CARD' | 'PIX' | 'BOLETO'
  ) => {
    setSelectedPaymentMethod(method);
  };

  // Buscar dados do checkout ao carregar a página
  useEffect(() => {
    const fetchCheckoutData = async () => {
      if (!id) {
        setError('ID do checkout não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await checkoutService.getCheckoutById(id);

        if (!response.success || !response.data) {
          throw new Error(response.error?.message || 'Checkout não encontrado');
        }

        // Verificar se o checkout está ativo
        if (!response.data.is_active || response.data.status !== 'PUBLISHED') {
          setError('Este checkout não está mais disponível');
          setLoading(false);
          return;
        }

        document.title = response.data.name;

        setCheckout(response.data);
      } catch (err) {
        logger.error('Erro ao carregar checkout:', err);

        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Erro inesperado ao carregar checkout');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCheckoutData();
  }, [id]);

  // Opções de parcelamento para cartão de crédito
  const creditCardInstallmentOptions = useMemo(() => {
    if (!checkout) return [];

    // Verificar se o checkout permite parcelamento no cartão de crédito
    const allowInstallments =
      checkout.allow_credit_card_installments || checkout.allow_installments;
    const maxInstallments = allowInstallments
      ? checkout.max_credit_card_installments || checkout.max_installments || 12
      : 1;

    // Gera as opções de parcelas usando credit_card_installment_fee_percentage do checkout
    return Array.from({ length: maxInstallments }, (_, i) => {
      const installment = i + 1;
      const baseValue = checkout.amount || 0;
      let total = baseValue;

      if (installment > 1) {
        // Usar taxa configurada no checkout ou fallback para campos legados
        const feePercentage =
          checkout.credit_card_installment_fee_percentage ||
          checkout.installment_fee_percentage ||
          0;
        const monthlyRate = feePercentage / 100;

        // Fórmula de juros compostos: total = baseValue * (1 + taxa) ^ (n - 1)
        total = baseValue * Math.pow(1 + monthlyRate, installment - 1);
      }

      // Valor de cada parcela
      const perInstallment = total / installment;

      return {
        installment,
        total: Number(total.toFixed(2)),
        perInstallment: Number(perInstallment.toFixed(2)),
      };
    });
  }, [checkout]);

  // Opções de parcelamento para boleto
  const boletoInstallmentOptions = useMemo(() => {
    if (!checkout) return [];

    // Verificar se o checkout permite parcelamento no boleto
    const allowInstallments = checkout.allow_boleto_installments;
    const maxInstallments = allowInstallments
      ? checkout.max_boleto_installments || 12
      : 1;

    // Gera as opções de parcelas usando boleto_installment_fee_percentage do checkout
    return Array.from({ length: maxInstallments }, (_, i) => {
      const installment = i + 1;
      const baseValue = checkout.amount || 0;
      let total = baseValue;

      if (installment > 1) {
        // Usar taxa configurada no checkout para boleto
        const feePercentage = checkout.boleto_installment_fee_percentage || 0;
        const monthlyRate = feePercentage / 100;

        // Fórmula de juros compostos: total = baseValue * (1 + taxa) ^ (n - 1)
        total = baseValue * Math.pow(1 + monthlyRate, installment - 1);
      }

      // Valor de cada parcela
      const perInstallment = total / installment;

      return {
        installment,
        total: Number(total.toFixed(2)),
        perInstallment: Number(perInstallment.toFixed(2)),
      };
    });
  }, [checkout]);

  // Opções de parcelamento baseadas no método selecionado
  const installmentOptions = useMemo(() => {
    switch (selectedPaymentMethod) {
      case 'CREDIT_CARD':
        return creditCardInstallmentOptions;
      case 'BOLETO':
        return boletoInstallmentOptions;
      case 'PIX':
      default:
        // PIX sempre à vista
        return [
          {
            installment: 1,
            total: checkout?.amount || 0,
            perInstallment: checkout?.amount || 0,
          },
        ];
    }
  }, [
    selectedPaymentMethod,
    creditCardInstallmentOptions,
    boletoInstallmentOptions,
    checkout?.amount,
  ]);

  // Estados de loading e error
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="text-gray-600">Carregando checkout...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="mx-auto max-w-md p-6 text-center">
          <div className="rounded-lg border border-red-200 bg-red-50 p-6">
            <svg
              className="mx-auto mb-4 h-12 w-12 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="mb-2 text-lg font-semibold text-red-800">
              Erro ao carregar checkout
            </h2>
            <p className="mb-4 text-red-600">{error}</p>
            <button
              onClick={() => navigate('/home')}
              className="rounded-lg bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700"
            >
              Voltar ao início
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!checkout) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <p className="text-gray-600">
            Nenhum dado encontrado para este checkout
          </p>
        </div>
      </div>
    );
  }

  // Pegar imagem de banner dos attachments
  const bannerImage = checkout.attachments?.find(
    attachment => attachment.attachment_type === 'banner'
  )?.url;

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Conteúdo principal */}
      <div className="relative z-10 flex-1">
        <div className="mx-auto max-w-7xl px-4 pb-8 pt-16 sm:px-6 lg:px-8">
          {/* Banner de fundo */}
          <div className="inset-x-0 top-0 z-0 mx-auto mb-16 h-80 overflow-hidden rounded-2xl">
            {bannerImage ? (
              <div className="relative h-full">
                {/* Imagem de banner */}
                <img
                  src={bannerImage}
                  alt={`Banner ${checkout.name}`}
                  className="h-full w-full object-cover"
                  onError={e => {
                    // Fallback para gradiente em caso de erro
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.classList.add(
                        'bg-gradient-to-br',
                        'from-blue-600',
                        'via-blue-700',
                        'to-indigo-800'
                      );
                    }
                  }}
                />
                {/* Gradiente sobre a imagem para melhor integração */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-transparent to-indigo-800/30"></div>
              </div>
            ) : (
              /* Fallback - gradiente original com ondas */
              <div className="h-full bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800">
                {/* Elementos decorativos do banner */}
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute -left-20 -top-20 h-40 w-40 rounded-full bg-white/10"></div>
                  <div className="absolute -right-32 top-10 h-64 w-64 rounded-full bg-white/5"></div>
                  <div className="absolute left-1/2 top-1/2 h-32 w-32 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/5"></div>
                </div>

                {/* Padrão de ondas - apenas quando não há imagem */}
                <svg
                  className="absolute bottom-0 left-0 w-full text-gray-50"
                  viewBox="0 0 1440 120"
                  fill="currentColor"
                >
                  <path d="M0,64L48,69.3C96,75,192,85,288,80C384,75,480,53,576,48C672,43,768,53,864,64C960,75,1056,85,1152,80C1248,75,1344,53,1392,42.7L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"></path>
                </svg>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse gap-0 lg:grid lg:grid-cols-12 lg:gap-8">
            {/* Formulário de checkout */}
            <div className="mt-8 animate-fade-in-up [animation-delay:0.1s] md:mt-0 lg:col-span-7">
              <div className="rounded-xl border border-white/50 bg-white shadow-xl backdrop-blur-sm transition-all duration-300">
                <div className="px-6 py-8">
                  <CheckoutForm
                    onCouponChange={handleCouponChange}
                    onPaymentMethodChange={handlePaymentMethodChange}
                    data={checkout}
                    // finalValue={
                    //   appliedCoupon.isApplied
                    //     ? (checkout.amount || checkout.max_amount) *
                    //       (1 - appliedCoupon.discount / 100)
                    //     : checkout.amount || checkout.max_amount
                    // }
                    installments={installmentOptions}
                  />
                </div>
              </div>
            </div>

            {/* Sidebar com produto e resumo integrados */}
            <div className="animate-slide-in-right [animation-delay:0.2s] lg:col-span-5 lg:mt-0">
              {/* Container sticky para acompanhar o scroll */}
              <div className="sticky top-8">
                <ProductCard
                  data={checkout}
                  coupon={appliedCoupon}
                  installments={installmentOptions}
                  selectedPaymentMethod={selectedPaymentMethod}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t bg-white/80 shadow-lg backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex animate-fade-in-up flex-col items-center justify-between space-y-4 md:flex-row md:space-y-0">
            {/* <div className="flex items-center space-x-4 text-sm text-gray-600">
              <span className="flex items-center transition-colors duration-300 hover:text-gray-800">
                <svg
                  className="mr-1 h-4 w-4 transition-transform duration-300 hover:scale-110"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Suporte 24/7
              </span>
              <span className="opacity-50">•</span>
              <span className="transition-colors duration-300 hover:text-gray-800">
                Garantia de 7 dias
              </span>
              <span className="opacity-50">•</span>
              <span className="cursor-pointer transition-colors duration-300 hover:text-gray-800">
                Política de privacidade
              </span>
            </div> */}
            <div className="text-sm text-gray-500 transition-colors duration-300 hover:text-gray-700">
              © {new Date().getFullYear()}{' '}
              <a
                href="https://multimoney.app.br"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-blue-500"
              >
                Multimoney
              </a>
              . Todos os direitos reservados.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
